import { RouteObject } from 'react-router-dom'
import { Navigate } from 'react-router-dom'

// Root pages
import { HomePage } from '@/pages/root/HomePage'
import { AboutPage } from '@/pages/root/AboutPage'

// Main section pages
import { SoftwarePage } from '@/pages/software/SoftwarePage'
import { InvestmentPage } from '@/pages/services/financial/InvestmentPage.js'
import { ServicesPage } from '@/pages/services/ServicesPage'
import { InsightsPage } from '@/pages/insights/InsightsPage'
import { CommunityPage } from '@/pages/community/CommunityPage'

// Software pages
import {
    DigitalFundsPage,
    ETPPage,
    FarmlandsPage,
    REITsPage,
    CreditPage,
    OpportunityZonesPage,
    BondsPage,
    InfrastructurePage,
    VolatilityPage,
    CommoditiesPage,
    MediaPage,
    SmartCitiesPage,
    AlternativesPage
} from '@/pages/software/digital-funds'

import {
    InstitutionalPage,
    SDIPage,
    SyphenPage,
    CreditosnPage,
    ESGPage
} from '@/pages/software/institutional'

// Investment pages
import {
    StrategiesPage,
    VenturePage,
    PrivateEquityPage,
    QuantitativePage,
    SystematicEquityPage,
    FundamentalEquityPage,
    FissionFusionPage
} from '@/pages/investment/strategies'

import {
    InstrumentsPage,
    DerivativesPage,
    CommoditiesContractsPage
} from '@/pages/investment/instruments'

// Shared components (used across multiple sections)
import { ConsultingPage } from '@/pages/services/consulting/ConsultingPage'
import { StartupConsultingPage } from '@/pages/services/consulting/StartupConsultingPage'
import { AcademicResearchPage } from '@/pages/services/research/AcademicResearchPage'
import { ProcessOptimizationPage } from '@/pages/services/consulting/ProcessOptimizationPage'
import { DigitalTransformationPage } from '@/pages/services/consulting/DigitalTransformationPage'
import { ResearchPapersPage } from '@/pages/insights/reports/ResearchPapersPage'
import { LearningResourcesPage } from '@/pages/insights/reports/LearningResourcesPage'
import { ReportsPage } from '@/pages/insights/ReportsPage'
import { RevenueCreationPage } from '@/pages/services/consulting/RevenueCreationPage'
import { GovernmentResearchPage } from '@/pages/services/research/GovernmentResearchPage'
import { ResearchPage } from '@/pages/services/research/ResearchPage'
import { PrivateResearchPage } from '@/pages/services/research/PrivateResearchPage'

// Update financial services imports
import {
    LifeInsurancePage,
    InstitutionalBankingPage,
    PortfolioPage,
    WealthPage,
    RSUPage,
    EquityInsurancePage,
    LendingPage,
    RealEstatePage,
    FranchisePage,
    DistressedREPage,
    AssetManagementPage
} from '@/pages/services/financial'
import { IndustryReportsPage } from '@/pages/insights/reports/IndustryReportsPage'
import { FinancialNewsPage } from '@/pages/insights/reports/FinancialNewsPage'
import { BlogPage } from '@/pages/insights/blog/BlogPage'
import { OpinionPage } from '@/pages/insights/blog/OpinionPage'
import { PartnerInsightsPage } from '@/pages/insights/blog/PartnerInsightsPage'
import { AnnouncementsPage } from '@/pages/insights/blog/AnnouncementsPage'
import { FuelRealityPage } from '@/pages/community/FuelRealityPage'
import { CryptoCreditUnionPage } from '@/pages/community/CryptoCreditUnionPage'
import { AlphaZillenialPage } from '@/pages/community/AlphaZillenialPage'
import { FreelancerFundPage } from '@/pages/community/FreelancerFundPage'

// Software section imports
import {
    // Vertical Solutions
    VerticalSolutionsPage,
    InsuranceAndBenefitsNetworkPage,
    CleoPage,
    CheckaCVPage,
    SSimpleAuthPage,
    SentiumPage,
    LectuPage,
    ScavangerPage,
    FlotaPage,
    OveltoPage,
    TintaPage
} from '@/pages/software/vertical'

import {
    // APIs & Embedables
    APIPage,
    InsuranceAPIPage,
    StableCoinsAPIPage
} from '@/pages/software/api'

import {
    // DarDen Apps
    DarDenAppsPage,
    DarDenAppPage,
    TratoPage,
    PortalPage,
    SambbiPage,
    TiendaPage,
    MGTPage,
    OnWheelsPage,
    TroppaPage,
    DeneflitzPage,
    MercantiPage,
    RessiPage,
    RoomAidPage,
    FlyTrapPage,
    SprinklePage
} from '@/pages/software/darden'
import { InvestmentServicesPage } from '@/pages/investment/services/InvestmentServicesPage'

// Import the pages
import { CareersPage } from '@/pages/CareersPage'
import { ContactPage } from '@/pages/ContactPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { TermsPage } from '@/pages/TermsPage'

export const routes: RouteObject[] = [
    // Root routes
    {
        path: '/',
        element: <HomePage />
    },
    {
        path: '/about',
        element: <AboutPage />
    },
    // A. Software & Technology
    {
        path: '/software/*',
        element: <SoftwarePage />,
        children: [
            {
                path: 'digital-funds/*',
                element: <DigitalFundsPage />,
                children: [
                    { path: 'etp', element: <ETPPage /> },
                    { path: 'farmlands', element: <FarmlandsPage /> },
                    { path: 'reits', element: <REITsPage /> },
                    { path: 'credit', element: <CreditPage /> },
                    { path: 'opportunity-zones', element: <OpportunityZonesPage /> },
                    { path: 'bonds', element: <BondsPage /> },
                    { path: 'infrastructure', element: <InfrastructurePage /> },
                    { path: 'volatility', element: <VolatilityPage /> },
                    { path: 'commodities', element: <CommoditiesPage /> },
                    { path: 'media', element: <MediaPage /> },
                    { path: 'smart-cities', element: <SmartCitiesPage /> },
                    { path: 'alternatives', element: <AlternativesPage /> }
                ]
            },
            {
                path: 'vertical/*',
                element: <VerticalSolutionsPage />,
                children: [
                    { path: 'insurance-and-benefits', element: <InsuranceAndBenefitsNetworkPage /> },
                    { path: 'cleo', element: <CleoPage /> },
                    { path: 'checkacv', element: <CheckaCVPage /> },
                    { path: 'ssimpleauth', element: <SSimpleAuthPage /> },
                    { path: 'sentium', element: <SentiumPage /> },
                    { path: 'lectu', element: <LectuPage /> },
                    { path: 'scavanger', element: <ScavangerPage /> },
                    { path: 'flota', element: <FlotaPage /> },
                    { path: 'ovelto', element: <OveltoPage /> },
                    { path: 'tinta', element: <TintaPage /> }
                ]
            },
            {
                path: 'institutional/*',
                element: <InstitutionalPage />,
                children: [
                    { path: 'sdi', element: <SDIPage /> },
                    { path: 'syphen', element: <SyphenPage /> },
                    { path: 'creditosn', element: <CreditosnPage /> },
                    { path: 'esg', element: <ESGPage /> }
                ]
            },
            {
                path: 'darden/*',
                element: <DarDenAppsPage />,
                children: [
                    { path: 'darden', element: <DarDenAppPage /> },
                    { path: 'trato', element: <TratoPage /> },
                    { path: 'portal', element: <PortalPage /> },
                    { path: 'sambbi', element: <SambbiPage /> },
                    { path: 'tienda', element: <TiendaPage /> },
                    { path: 'mgt', element: <MGTPage /> },
                    { path: 'onwheels', element: <OnWheelsPage /> },
                    { path: 'troppa', element: <TroppaPage /> },
                    { path: 'deneflitz', element: <DeneflitzPage /> },
                    { path: 'mercanti', element: <MercantiPage /> },
                    { path: 'ressi', element: <RessiPage /> },
                    { path: 'roomaid', element: <RoomAidPage /> },
                    { path: 'flytrap', element: <FlyTrapPage /> },
                    { path: 'sprinkle', element: <SprinklePage /> }
                ]
            },
            {
                path: 'api/*',
                element: <APIPage />,
                children: [
                    { path: 'embeddable-insurance', element: <InsuranceAPIPage /> },
                    { path: 'stable-coins', element: <StableCoinsAPIPage /> }
                ]
            }
        ]
    },
    {
        path: '/investment/*',
        element: <InvestmentPage />,
        children: [
            {
                path: 'digital-funds/*',
                element: <Navigate to="/software/digital-funds" replace />
            },
            {
                path: 'strategies/*',
                element: <StrategiesPage />,
                children: [
                    { path: 'venture', element: <VenturePage /> },
                    { path: 'pe', element: <PrivateEquityPage /> },
                    { path: 'quant', element: <QuantitativePage /> },
                    { path: 'systematic', element: <SystematicEquityPage /> },
                    { path: 'fundamental', element: <FundamentalEquityPage /> },
                    { path: 'energy', element: <FissionFusionPage /> }
                ]
            },
            {
                path: 'instruments/*',
                element: <InstrumentsPage />,
                children: [
                    { path: 'derivatives', element: <DerivativesPage /> },
                    { path: 'commodities', element: <CommoditiesContractsPage /> }
                ]
            },
            {
                path: 'services/*',
                element: <InvestmentServicesPage />,
                children: [
                    { path: 'banking', element: <InstitutionalBankingPage /> },
                    { path: 'insurance', element: <LifeInsurancePage /> },
                    { path: 'managed-portfolios', element: <PortfolioPage /> },
                    { path: 'private-wealth', element: <WealthPage /> },
                    { path: 'rsu-liquidity', element: <RSUPage /> },
                    { path: 'equity-compensation-insurance', element: <EquityInsurancePage /> },
                    { path: 'equity-backed-lending', element: <LendingPage /> },
                    { path: 'real-estate-financing', element: <RealEstatePage /> },
                    { path: 'green-franchises', element: <FranchisePage /> },
                    { path: 'distressed-real-estate', element: <DistressedREPage /> },
                    { path: 'asset-management', element: <AssetManagementPage /> }
                ]
            }
        ]
    },
    {
        path: '/services/*',
        element: <ServicesPage />,
        children: [
            {
                path: 'consulting/*',
                element: <ConsultingPage />,
                children: [
                    { path: 'startup-growth', element: <StartupConsultingPage /> },
                    { path: 'digital-transformation', element: <DigitalTransformationPage /> },
                    { path: 'process-optimization', element: <ProcessOptimizationPage /> },
                    { path: 'revenue-creation', element: <RevenueCreationPage /> }
                ]
            },
            {
                path: 'research/*',
                element: <ResearchPage />,
                children: [
                    { path: 'government', element: <GovernmentResearchPage /> },
                    { path: 'academic', element: <AcademicResearchPage /> },
                    { path: 'private', element: <PrivateResearchPage /> }
                ]
            },
            {
                path: 'financial/*',
                element: <Navigate to="/investment/services" replace />
            }
        ]
    },
    {
        path: '/insights/*',
        element: <InsightsPage />,
        children: [
            {
                path: 'reports/*',
                element: <ReportsPage />,
                children: [
                    { path: 'industry', element: <IndustryReportsPage /> },
                    { path: 'learning', element: <LearningResourcesPage /> },
                    { path: 'news', element: <FinancialNewsPage /> },
                    { path: 'research', element: <ResearchPapersPage /> }
                ]
            },
            {
                path: 'blog/*',
                element: <BlogPage />,
                children: [
                    { path: 'opinion', element: <OpinionPage /> },
                    { path: 'partner', element: <PartnerInsightsPage /> },
                    { path: 'announcements', element: <AnnouncementsPage /> }
                ]
            }
        ]
    },
    {
        path: '/community/*',
        element: <CommunityPage />,
        children: [
            { path: 'fuel-reality', element: <FuelRealityPage /> },
            { path: 'alpha-zillenial', element: <AlphaZillenialPage /> },
            { path: 'crypto-union', element: <CryptoCreditUnionPage /> },
            { path: 'freelancer-fund', element: <FreelancerFundPage /> }
        ]
    },
    // Add direct routes for all footer links
    { path: '/careers', element: <CareersPage /> },
    { path: '/contact', element: <ContactPage /> },
    { path: '/privacy', element: <PrivacyPage /> },
    { path: '/terms', element: <TermsPage /> },
    { path: '/cookies', element: <Navigate to="/privacy" replace /> }, // Temporary redirect
] 